import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, TrendingUp, Zap } from "lucide-react";
import { logger } from "@/lib/logger";

/**
 * Admin A/B testing tab: launch ±20% experiments on highest-volume categories,
 * watch per-arm unlocks/revenue/lift, end tests when done.
 */
export default function AdminABTests() {
  const [tests, setTests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [results, setResults] = useState({}); // test_id -> results
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/admin/ab-tests"),
        api.get("/admin/ab-tests/suggestions"),
      ]);
      setTests(t.data);
      setSuggestions(s.data);
      // Fetch results for active tests only (ended tests are frozen; still fetch on demand)
      const resMap = {};
      await Promise.all(
        t.data.map(async (test) => {
          try {
            const r = await api.get(`/admin/ab-tests/${test.test_id}/results`);
            resMap[test.test_id] = r.data;
          } catch (_) { /* ignore per-test errors */ }
        })
      );
      setResults(resMap);
    } catch (err) {
      logger.error("[ab-tests] refresh failed", err);
      toast.error("Failed to load A/B tests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const launch = async (category_id, delta_pct, slug) => {
    try {
      await api.post("/admin/ab-tests", { category_id, delta_pct });
      toast.success(`Launched ${delta_pct > 0 ? "+" : ""}${delta_pct}% test on ${slug}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to launch test");
    }
  };

  const end = async (test_id, slug) => {
    if (!window.confirm(`End the active A/B test on ${slug}? Traffic reverts to base price.`)) return;
    try {
      await api.post(`/admin/ab-tests/${test_id}/end`);
      toast.success(`Ended test on ${slug}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to end test");
    }
  };

  if (loading) return <p className="font-mono text-xs text-muted-foreground">Loading experiments…</p>;

  const activeTests = tests.filter((t) => t.active);
  const endedTests = tests.filter((t) => !t.active);

  return (
    <div className="space-y-6" data-testid="admin-ab-tests">
      <SuggestionsCard suggestions={suggestions} onLaunch={launch} />
      <ActiveTestsCard tests={activeTests} results={results} onEnd={end} />
      {endedTests.length > 0 && <HistoryCard tests={endedTests} results={results} />}
    </div>
  );
}

function SuggestionsCard({ suggestions, onLaunch }) {
  return (
    <div className="brut-card bg-white p-6">
      <div className="flex items-center gap-2 mb-4">
        <FlaskConical className="w-5 h-5" />
        <div className="overline">Suggested experiments · top-volume categories</div>
      </div>
      {!suggestions.length ? (
        <p className="font-mono text-xs text-muted-foreground">
          No categories with recent volume are available for testing. Post more jobs, or end active tests first.
        </p>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s) => (
            <div key={s.category_id} className="border-2 border-ink p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="font-display font-bold text-lg">{s.category_name}</div>
                <div className="font-mono text-xs text-muted-foreground">
                  {s.recent_jobs} jobs last 30d · {s.share_pct}% share · base ${s.base_price}
                </div>
              </div>
              <button
                onClick={() => onLaunch(s.category_id, 20, s.category_slug)}
                className="brut-btn-primary text-xs px-3 py-2"
                data-testid={`launch-plus20-${s.category_slug}`}
              >
                Launch +20% → ${s.variant_plus_20}
              </button>
              <button
                onClick={() => onLaunch(s.category_id, -20, s.category_slug)}
                className="brut-btn text-xs px-3 py-2"
                data-testid={`launch-minus20-${s.category_slug}`}
              >
                Launch −20% → ${s.variant_minus_20}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActiveTestsCard({ tests, results, onEnd }) {
  return (
    <div className="brut-card bg-white p-6">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-acid" />
        <div className="overline">Active experiments</div>
      </div>
      {!tests.length ? (
        <p className="font-mono text-xs text-muted-foreground">No active experiments. Launch one above.</p>
      ) : (
        <div className="space-y-4">
          {tests.map((t) => <TestRow key={t.test_id} test={t} result={results[t.test_id]} onEnd={onEnd} active />)}
        </div>
      )}
    </div>
  );
}

function HistoryCard({ tests, results }) {
  return (
    <div className="brut-card bg-white p-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5" />
        <div className="overline">Ended experiments</div>
      </div>
      <div className="space-y-4">
        {tests.map((t) => <TestRow key={t.test_id} test={t} result={results[t.test_id]} />)}
      </div>
    </div>
  );
}

function TestRow({ test, result, onEnd, active }) {
  const arms = result?.arms || { A: { unlocks: 0, revenue: 0, price: test.base_price }, B: { unlocks: 0, revenue: 0, price: test.variant_price } };
  const lift = result?.lift_pct;
  const total = result?.total_unlocks || 0;

  return (
    <div className="border-2 border-ink p-4" data-testid={`ab-test-${test.category_slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="font-display font-bold text-lg">{test.category_name}</div>
          <div className="font-mono text-xs text-muted-foreground">
            {test.delta_pct > 0 ? "+" : ""}{test.delta_pct}% · started {new Date(test.created_at).toLocaleDateString()}
            {test.ended_at && ` · ended ${new Date(test.ended_at).toLocaleDateString()}`}
          </div>
        </div>
        {active && (
          <button
            onClick={() => onEnd(test.test_id, test.category_slug)}
            className="brut-btn text-xs px-3 py-2"
            data-testid={`end-test-${test.category_slug}`}
          >
            End test
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-0 border-2 border-ink">
        <ArmCell label="A · Base" price={arms.A.price} unlocks={arms.A.unlocks} revenue={arms.A.revenue} />
        <ArmCell label="B · Variant" price={arms.B.price} unlocks={arms.B.unlocks} revenue={arms.B.revenue} last hot />
      </div>

      <div className="mt-3 flex flex-wrap gap-4 font-mono text-xs">
        <span>Total unlocks · <b>{total}</b></span>
        <span>Total revenue · <b>${(result?.total_revenue ?? 0).toFixed(2)}</b></span>
        {lift != null && (
          <span className={lift >= 0 ? "text-green-700" : "text-red-700"}>
            Revenue/unlock lift · <b>{lift > 0 ? "+" : ""}{lift}%</b>
          </span>
        )}
        {lift == null && total > 0 && (
          <span className="text-muted-foreground">Need traffic on both arms for lift %</span>
        )}
      </div>
    </div>
  );
}

function ArmCell({ label, price, unlocks, revenue, last, hot }) {
  return (
    <div className={`p-4 ${hot ? "bg-acid" : "bg-white"} ${!last ? "border-r-2 border-ink" : ""}`}>
      <div className="overline text-xs">{label}</div>
      <div className="font-display font-black text-2xl mt-1">${Number(price).toFixed(2)}</div>
      <div className="font-mono text-xs mt-1">
        {unlocks} unlocks · ${Number(revenue).toFixed(2)} rev
      </div>
    </div>
  );
}
